import React, { useEffect, useState } from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import ColoredButton from 'components/ColoredButton';
import type { ActiveRopaReviewMetrics } from 'config/api/ropaReviews/ropaReviews.types';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';

type Props = {
  loading?: boolean;
  metrics?: ActiveRopaReviewMetrics;
  onClose: () => void;
  onConfirm: (comment: string) => void;
  open: boolean;
};

const CompleteRopaReviewModal: React.FC<Props> = ({ loading = false, metrics, onClose, onConfirm, open }) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setComment('');
      setError('');
    }
  }, [open]);

  const incompleteCount =
    (metrics?.awaitingResponseCount ?? 0) + (metrics?.submittedCount ?? 0) + (metrics?.clarificationRequestedCount ?? 0);

  const handleClose = () => {
    setComment('');
    setError('');
    onClose();
  };

  const handleConfirm = () => {
    if (!comment.trim()) {
      setError(t(validation_messages.required));
      return;
    }

    onConfirm(comment.trim());
  };

  return (
    <Dialog fullWidth maxWidth='sm' onClose={handleClose} open={open}>
      <DialogTitle>{t(processing_activities_messages.asset_ropa.modals.complete_review.title)}</DialogTitle>
      <DialogContent>
        <Typography className='' color='textSecondary' gutterBottom variant='body2'>
          {t(processing_activities_messages.asset_ropa.modals.complete_review.summary_intro)}
        </Typography>
        <ul>
          <li>
            {t(processing_activities_messages.asset_ropa.modals.complete_review.accepted_count, {
              count: metrics?.acceptedCount ?? 0,
            })}
          </li>
          <li>
            {t(processing_activities_messages.asset_ropa.modals.complete_review.submitted_count, {
              count: metrics?.submittedCount ?? 0,
            })}
          </li>
          <li>
            {t(processing_activities_messages.asset_ropa.modals.complete_review.awaiting_response_count, {
              count: metrics?.awaitingResponseCount ?? 0,
            })}
          </li>
          <li>
            {t(processing_activities_messages.asset_ropa.modals.complete_review.clarification_requested_count, {
              count: metrics?.clarificationRequestedCount ?? 0,
            })}
          </li>
          {incompleteCount > 0 && (
            <li>
              {t(processing_activities_messages.asset_ropa.modals.complete_review.rows_to_cancel_count, {
                count: incompleteCount,
              })}
            </li>
          )}
        </ul>
        <Typography color='textSecondary' gutterBottom variant='body2'>
          {t(processing_activities_messages.asset_ropa.modals.complete_review.disclaimer)}
        </Typography>
        <TextField
          autoFocus
          error={Boolean(error)}
          fullWidth
          helperText={error || t(processing_activities_messages.asset_ropa.modals.complete_review.comment_help_required)}
          label={t(processing_activities_messages.asset_ropa.modals.complete_review.comment)}
          margin='normal'
          multiline
          onChange={event => {
            setComment(event.target.value);
            if (error) setError('');
          }}
          rows={4}
          value={comment}
          variant='outlined'
        />
      </DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='grey'
          disabled={loading}
          icon={false}
          onClick={handleClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel)}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={loading}
          icon={false}
          onClick={handleConfirm}
          showLoader={loading}
          textLike={false}
          variant='contained'
        >
          {t(processing_activities_messages.asset_ropa.modals.complete_review.confirm)}
        </ColoredButton>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteRopaReviewModal;
